import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetAllApplication,
  GetAllUser,
  getAssignUser,
  GetResources,
  Lock_UnlockAccount,
} from "../api/ssoapi/UserControlapi";
import { CTargetResource } from "../constants/CLocalStorage";
import {
  getApiFailState,
  getApiInitState,
  getApiLoadingState,
  getApiSuccessState,
} from "../api/notifyapi/APIShared";
import { IAPIState } from "../api/notifyapi/Notifyapi";
import { userDBTilte } from "../components/Mangement/userController/UserFilterPopup";
import { CUserRoles } from "../components/Constants/UserConstants";
import { ESearchFilterActiveTypes } from "../constants/CommonInterfaces";
import { HubConnection } from "@microsoft/signalr";

interface ITargetResource {
  TargetResource: IResources[];
  Status: string;
}
export interface IResources {
  label: string;
  value: string;
}
export interface IFilterResponse {
  data: IUser[];
  page: number;
  pageSize: number;
  totalCount: number;
  sortBy: string;
}
interface AdminActionItem {
  Action: string;
  performedBy?: string;
  PerformedOn: string | Date;
}

interface  UserAccount {
  Reason?: string;
  Account: string;
  KeyLimit?: number;
  AdminAction?: AdminActionItem[];
}

export interface IUser {
  user: any;
  userAccount: any;
  UserAccount:any;
  actionDetails: any;
  lastAction: any;
  createdOn: string | number | Date;
  id: string;
  userName: string;
  email: string;
  isOtpEnabled: string;
  account: string;
  ccEmailIds: string[];
  roles: string[];
  ssoRoles: string[];
}
export interface IApplication {
  id: string;
  name: string;
  description: string;
  displayState: string;
}

export interface IAssignList {
  emailID: string;
  name: string;
  userID: string;
}

export interface IColumFilter {
  value: any;
  isFiltered: boolean;
  sort: "asc" | "des" | "none";
}

export interface IUserLocalFilterState {
  userName: IColumFilter;
  email: IColumFilter;
  accessApplications: IColumFilter;
  ssoRoles: IColumFilter;
  otp: IColumFilter;
  accountStatus: IColumFilter;
  ccEmailIds: IColumFilter;
}

export const userCloumnFilter: IUserLocalFilterState = {
  accessApplications: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  accountStatus: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  ccEmailIds: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  email: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  otp: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  ssoRoles: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
  userName: {
    value: "",
    isFiltered: false,
    sort: "none",
  },
};
export interface IUserFilter {
  field: string;
  operator: string;
  value: any;
}

export interface IUserFilterModel {
  filters: IUserFilter[];
  isAscending: boolean;
  isOr: boolean;
  page: number;
  pageSize: number;
  currentPageSize: number;
  sortField: string;
  isPageChanged: string;
}
export interface IGeneratorPassword {
  generatorId: string;
  generatorEmail: string;
}
export interface IUserGlobalFilterModel {
  filters: IUserFilter[];
  isOr: boolean;
}
interface IUserControlState {
  api: IAPIState;
  NotificationTarget: ITargetResource;
  userControlTable: IUser[];
  Application: IApplication[];
  UserAssignList: IAssignList[];
  totalCount: number;
  searchFilter: {
    globalSearch: {
      value: string;
      isActive: boolean;
    };
    columnSearch: IUserLocalFilterState;
    tempColumnSearch: IUserLocalFilterState;
    apiQuery: IUserFilterModel;
    globalSearchApiQuery: IUserGlobalFilterModel;
    lastActiveSearch: ESearchFilterActiveTypes;
  };
  generatePassword: IGeneratorPassword;
  enablePasswordGenerator: boolean;
  connection: HubConnection | null;
}
const initialState: IUserControlState = {
  enablePasswordGenerator: false,
  api: getApiInitState(),
  NotificationTarget: { TargetResource: [], Status: "" },
  userControlTable: [],
  Application: [],
  UserAssignList: [],
  searchFilter: {
    globalSearch: { value: "", isActive: false },
    apiQuery: {
      filters: [],
      isAscending: false,
      isOr: false,
      page: 1,
      pageSize: 10,
      sortField: "",
      currentPageSize: 0,
      isPageChanged: "",
    },
    columnSearch: userCloumnFilter,
    tempColumnSearch: userCloumnFilter,
    globalSearchApiQuery: {
      filters: [],
      isOr: false,
    },
    lastActiveSearch: ESearchFilterActiveTypes.none,
  },
  totalCount: 0,
  generatePassword: {
    generatorEmail: "",
    generatorId: "",
  },
  connection: null,
};

export const UserControlSlice = createSlice({
  name: "UserControl",
  initialState: initialState,
  reducers: {
    GetConnection: (
      state,
      action: PayloadAction<{ connection: HubConnection }>
    ) => {
      console.log(action.payload);
      state.connection = action.payload.connection;
    },
    ApplyColumnSearchFilter: (state) => {
      // Copy columnSearch to tempColumnSearch
      state.searchFilter.tempColumnSearch = Object.assign(
        {},
        state.searchFilter.columnSearch
      );
      // Clear existing filters for the applied columns
      const fieldsToCheck: (keyof typeof userDBTilte)[] = [
        "userName",
        "email",
        "ssoRoles",
        "accessApplications",
        "accountStatus",
        "otp",
        "ccEmailIds",
      ];

      // Remove existing filters for the columns being filtered
      state.searchFilter.apiQuery.filters =
        state.searchFilter.apiQuery.filters.filter(
          (filter) =>
            !fieldsToCheck.some((field) => filter.field === userDBTilte[field])
        );

      // Add new filters based on tempColumnSearch
      fieldsToCheck.forEach((field) => {
        const localField =
          state.searchFilter.tempColumnSearch[
            field as keyof IUserLocalFilterState
          ];
        if (localField.value) {
          let operator = "contains";
          let value = localField.value;

          if (
            field === "otp" ||
            field === "accountStatus" ||
            field === "accessApplications"
          ) {
            operator = "eq";
          }

          state.searchFilter.apiQuery.filters.push({
            field: userDBTilte[field],
            operator: operator,
            value: value,
          });
        }
      });
    },
    UpdateSearchQuery: (state, action: PayloadAction<IUserFilter>) => {
      // Trim the value to check for empty strings
      const trimmedValue =
        typeof action.payload.value === "string"
          ? action.payload.value.trim()
          : action.payload.value;

      // Skip adding filter for empty values
      const isEmptyValue =
        trimmedValue === "" ||
        trimmedValue === null ||
        trimmedValue === undefined;

      if (isEmptyValue) {
        // If the value is empty, remove any existing filter for this field
        state.searchFilter.apiQuery.filters =
          state.searchFilter.apiQuery.filters.filter(
            (item) => item.field !== action.payload.field
          );
        return;
      }

      const existingFilterIndex = state.searchFilter.apiQuery.filters.findIndex(
        (item: IUserFilter) => item.field === action.payload.field
      );

      if (action.payload.value === "ALL") {
        // If the value is "ALL", remove the filter for that field
        state.searchFilter.apiQuery.filters =
          state.searchFilter.apiQuery.filters.filter(
            (item) => item.field !== action.payload.field
          );
      } else {
        if (existingFilterIndex !== -1) {
          // Update the existing filter's value
          state.searchFilter.apiQuery.filters[existingFilterIndex].value =
            trimmedValue;
        } else {
          // Add the new filter to the list
          state.searchFilter.apiQuery.filters.push({
            ...action.payload,
            value: trimmedValue,
          });
        }
      }
    },
    UpdateSorting: (
      state,
      action: PayloadAction<{ sortField: string; isAscending: boolean }>
    ) => {
      state.searchFilter.apiQuery.isAscending = action.payload.isAscending;
      state.searchFilter.apiQuery.sortField = action.payload.sortField;
      state.searchFilter.apiQuery.page = 1;
    },
    UpdateGlobalSearchFilter: (
      state,
      action: PayloadAction<{
        value: any;
      }>
    ) => {
      state.searchFilter.globalSearch.value = action.payload.value;
    },
    UpdateGlobalSearchApiQuery: (
      state,
      action: PayloadAction<{
        value: any;
        needToSetActive: boolean | undefined;
      }>
    ) => {
      if (action.payload.value.length > 0) {
        state.searchFilter.globalSearchApiQuery.filters = [
          {
            field: userDBTilte.userName,
            operator: "contains",
            value: action.payload.value,
          },
          {
            field: userDBTilte.email,
            operator: "contains",
            value: action.payload.value,
          },
          {
            field: userDBTilte.ssoRoles,
            operator: "contains",
            value: action.payload.value,
          },
          {
            field: userDBTilte.ccEmailIds,
            operator: "contains",
            value: action.payload.value,
          },
          {
            field: userDBTilte.accessApplications,
            operator: "contains",
            value: action.payload.value,
          },
        ];
        state.searchFilter.globalSearchApiQuery.isOr = true;
      } else {
        state.searchFilter.globalSearchApiQuery.filters =
          initialState.searchFilter.globalSearchApiQuery.filters;
        state.searchFilter.globalSearchApiQuery.isOr = false;
      }
      if (action.payload.needToSetActive) {
        if (state.searchFilter.globalSearch.value?.length > 0) {
          state.searchFilter.globalSearch.isActive = true;
        } else {
          state.searchFilter.globalSearch.isActive = false;
        }
      }
    },
    UpdateGlobalSearchFilterActive: (state) => {
      state.searchFilter.apiQuery.page = 1;
      if (state.searchFilter.globalSearch.value?.length > 0) {
        state.searchFilter.globalSearch.isActive = true;
      } else {
        state.searchFilter.globalSearch.isActive = false;
      }
    },
    UpdatePagination: (
      state,
      action: PayloadAction<{
        page: number;
        pageSize: number;
        isPageChanged: boolean;
      }>
    ) => {
      state.searchFilter.apiQuery.page = action.payload.page;
      state.searchFilter.apiQuery.pageSize = action.payload.pageSize;

      if (action.payload.isPageChanged) {
        state.searchFilter.apiQuery.isPageChanged = new Date().toTimeString();
      }
    },
    UpdateLocalSearchQuery: (
      state,
      action: PayloadAction<{ field: string; value: any }>
    ) => {
      let t: keyof IUserLocalFilterState;

      for (t in state.searchFilter.columnSearch) {
        if (t === action.payload.field) {
          state.searchFilter.columnSearch[t].value = action.payload.value;

          if (state.searchFilter.columnSearch[t].value.length > 0) {
            state.searchFilter.columnSearch[t].isFiltered = true;
          } else {
            state.searchFilter.columnSearch[t].isFiltered = false;
          }
        }
      }
    },
    UpdateLastActiveSearch: (
      state,
      action: PayloadAction<{ value: ESearchFilterActiveTypes }>
    ) => {
      state.searchFilter.lastActiveSearch = action.payload.value;
    },
    resetLocalSearchQuery: (
      state,
      action: PayloadAction<{
        canClearAll: boolean;
        fieldClear: string;
      }>
    ) => {
      if (action.payload.canClearAll) {
        // Reset entire column search to initial state
        state.searchFilter.columnSearch =
          initialState.searchFilter.columnSearch;

        // Reset API query filters to initial state
        state.searchFilter.apiQuery.filters =
          initialState.searchFilter.apiQuery.filters;
      } else if (action.payload.fieldClear?.length > 0) {
        let t: keyof IUserLocalFilterState;

        for (t in state.searchFilter.columnSearch) {
          if (t === action.payload.fieldClear) {
            // Reset specific column search value and filtered status
            state.searchFilter.columnSearch[t].value = "";
            state.searchFilter.columnSearch[t].isFiltered = false;

            // Remove corresponding filter from apiQuery filters using userDBTilte mapping
            state.searchFilter.apiQuery.filters =
              state.searchFilter.apiQuery.filters.filter(
                (filter) =>
                  filter.field !== userDBTilte[t as keyof typeof userDBTilte]
              );
          }
        }
      }
    },
    UpdateDataAfterLogout: (state) => {
      state.searchFilter.apiQuery = initialState.searchFilter.apiQuery;
      state.searchFilter.columnSearch = userCloumnFilter;
      state.searchFilter.globalSearch = initialState.searchFilter.globalSearch;
      state.searchFilter.globalSearchApiQuery =
        initialState.searchFilter.globalSearchApiQuery;
      state.searchFilter.tempColumnSearch =
        initialState.searchFilter.tempColumnSearch;
    },
    UpdateEnableGeneratePassword: (state, action: PayloadAction<boolean>) => {
      state.enablePasswordGenerator = action.payload;
    },
    UpdateGeneratePassword: (
      state,
      action: PayloadAction<IGeneratorPassword>
    ) => {
      state.generatePassword.generatorEmail = action.payload.generatorEmail;
      state.generatePassword.generatorId = action.payload.generatorId;
    },
    ResetColumnSearchFilter: (
      state,
      action: PayloadAction<{
        canClearAll: boolean;
        fieldClear: string;
      }>
    ) => {
      // Reset temp column search
      if (action.payload.canClearAll) {
        // Reset everything as before
        state.searchFilter.tempColumnSearch =
          initialState.searchFilter.tempColumnSearch;
      } else if (action.payload.fieldClear?.length > 0) {
        // Clear column search value and isFiltered
        let t: keyof IUserLocalFilterState;
        for (t in state.searchFilter.tempColumnSearch) {
          if (t === action.payload.fieldClear) {
            // Reset specific column search value and filtered status
            state.searchFilter.tempColumnSearch[t].value = "";
            state.searchFilter.tempColumnSearch[t].isFiltered = false;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetResources.pending, (state, action) => {
        state.api = getApiLoadingState();
        state.NotificationTarget.Status = CTargetResource.LOADING;
      })
      .addCase(
        GetResources.fulfilled,
        (state, action: PayloadAction<IResources[]>) => {
          state.api = getApiSuccessState();
          state.NotificationTarget.Status = CTargetResource.SUCCESS;
          state.NotificationTarget.TargetResource = action.payload;
        }
      )
      .addCase(GetResources.rejected, (state, action) => {
        state.api = getApiFailState();
        state.NotificationTarget.Status = CTargetResource.FAILED;
      })

      .addCase(GetAllUser.pending, (state, action) => {
        state.api = getApiLoadingState();
      })
      .addCase(
        GetAllUser.fulfilled,
        (state, action: PayloadAction<IFilterResponse>) => {
          state.api = getApiSuccessState();
          state.userControlTable = prepareUserControlData(action.payload.data);
          state.searchFilter.apiQuery.page = action.payload.page;
          state.searchFilter.apiQuery.currentPageSize =
            action.payload.pageSize > 0 ? action.payload.pageSize : 10;
          state.searchFilter.apiQuery.sortField = action.payload.sortBy;
          state.totalCount = action.payload.totalCount;
        }
      )
      .addCase(GetAllUser.rejected, (state, action) => {
        state.api = getApiFailState();
      })

      .addCase(GetAllApplication.pending, (state, action) => {
        state.api = getApiLoadingState();
      })
      .addCase(
        GetAllApplication.fulfilled,
        (state, action: PayloadAction<IApplication[]>) => {
          state.api = getApiSuccessState();
          state.Application = action.payload;
        }
      )
      .addCase(GetAllApplication.rejected, (state, action) => {
        state.api = getApiFailState();
      })

      .addCase(getAssignUser.pending, (state, action) => {
        state.api = getApiLoadingState();
      })
      .addCase(
        getAssignUser.fulfilled,
        (state, action: PayloadAction<IAssignList[]>) => {
          state.api = getApiSuccessState();
          state.UserAssignList = action.payload;
        }
      )
      .addCase(getAssignUser.rejected, (state, action) => {
        state.api = getApiFailState();
      })

      .addCase(Lock_UnlockAccount.pending, (state, action) => {
        state.api = getApiLoadingState();
      })
      .addCase(
        Lock_UnlockAccount.fulfilled,
        (state, action: PayloadAction<IAssignList[]>) => {
          state.api = getApiSuccessState();
          state.UserAssignList = action.payload;
        }
      )
      .addCase(Lock_UnlockAccount.rejected, (state, action) => {
        state.api = getApiFailState();
      });
  },
});

const prepareUserControlData = (userData: IUser[]): IUser[] => {
  let outData: IUser[] = [];

  if (userData) {
    userData.map((user) => {
      let lUser = user;

      // Map UserAccount to userAccount if it exists
      if (user.UserAccount && !user.userAccount) {
        lUser.userAccount = user.UserAccount;
      }

      lUser.ssoRoles = lUser.ssoRoles.map((rol) => {
        let outRole = rol;
        var existRole = CUserRoles.find((x) => x.key == rol);
        if (existRole) {
          outRole = existRole.Label;
        }
        return outRole;
      });
      outData.push(lUser);
    });
  }

  return outData;
};
export const UserControlSlices = UserControlSlice.actions;
export default UserControlSlice.reducer;
